import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import {
  ACTIVE_STATUS,
  STATUS,
} from "@circle-react/helpers/communityMemberSubscriptionHelpers";
import { updateOrRemoveQueryParam } from "@circle-react/helpers/urlHelpers";
import { useAdminPaywallSubscription } from "@circle-react/hooks/paywalls/useAdminPaywallSubscription";
import { paywallSubscriptionQueryString } from "@circle-react/hooks/paywalls/useAdminPaywallSubscription";
import { useRouterQueryParams } from "@circle-react/hooks/useRouterQueryParams";
import { useFilterUrl } from "@circle-react-shared/Filter";
import {
  allFilterTranslations,
  normalizeFilters,
} from "../../Filters/useFilters";
import { getPaginationLimitThreshold } from "../../utils";
import { RESULTS_PER_PAGE, TABS } from "../constants";
import type { TabType } from "../constants";

interface OrderParam {
  created_at: "desc";
}

interface QueryParams {
  page?: string;
  search_key?: string;
  status?: string;
  tab?: string;
}

const defaultOrder: OrderParam = { created_at: "desc" };

const statusByTab: Record<string, string | null> = {
  [TABS.ALL]: null,
  [TABS.ACTIVE]: ACTIVE_STATUS.join(","),
  [TABS.CANCELED]: STATUS.CANCELED,
};

export const usePaywallSubscriptionsAdmin = () => {
  const history = useHistory();
  const queryParams: QueryParams = useRouterQueryParams();
  const { removeQueryParams } = useFilterUrl();
  const {
    tab = TABS.ALL,
    search_key = "",
    page: pageParam = 1,
    status,
  } = queryParams;

  const [activeFilterTab, setActiveFilterTab] = useState<TabType>(
    (Object.values(TABS) as string[]).includes(tab)
      ? (tab as TabType)
      : TABS.ALL,
  );
  const [searchKey, setSearchKey] = useState<string>(search_key);
  const [page, setPage] = useState<number>(
    getPaginationLimitThreshold(pageParam),
  );

  const updateQueryParam = (
    keyQueryParam: string,
    value: string | number,
  ): void => {
    const newQueryParams = updateOrRemoveQueryParam(
      keyQueryParam,
      String(value),
    );
    history.push(`?${newQueryParams}`);
  };

  const clearAllFilters = (): void => {
    const filterKeys = Object.keys(allFilterTranslations.subscriptions);
    removeQueryParams(filterKeys);
  };

  const updatePage = (value: number): void => {
    updateQueryParam("page", String(value));
    setPage(value);
  };

  const setActiveFilter = (tab: string): void => {
    clearAllFilters();
    const tabType = (Object.values(TABS) as string[]).includes(tab)
      ? (tab as TabType)
      : TABS.ALL;
    setActiveFilterTab(tabType);
    updateQueryParam("status", statusByTab[tab] || "");
    updateQueryParam("tab", tab);
  };

  // This function blocks calling our API when the tab is not activated
  const getSearchKey = (
    currentTab: string,
    tabTarget: string,
    searchKey: string,
  ): string => (currentTab === tabTarget ? searchKey : "");

  useEffect(() => {
    updateQueryParam("tab", activeFilterTab);
    if (!status) {
      updateQueryParam("status", statusByTab[activeFilterTab] || "");
    }
  }, []);

  const allSubscriptions = useAdminPaywallSubscription({
    page,
    perPage: RESULTS_PER_PAGE,
    orderParam: defaultOrder,
    searchKeyParams: getSearchKey(activeFilterTab, TABS.ALL, searchKey),
    filters: normalizeFilters(queryParams, allFilterTranslations.subscriptions),
    enabled: activeFilterTab === TABS.ALL,
    setPage: updatePage,
  });
  const activeSubscriptions = useAdminPaywallSubscription({
    page,
    perPage: RESULTS_PER_PAGE,
    orderParam: defaultOrder,
    searchKeyParams: getSearchKey(activeFilterTab, TABS.ACTIVE, searchKey),
    filters: normalizeFilters(
      {
        ...queryParams,
      },
      allFilterTranslations.subscriptions,
    ),
    enabled: activeFilterTab === TABS.ACTIVE,
    setPage: updatePage,
  });
  const canceledSubscriptions = useAdminPaywallSubscription({
    page,
    perPage: RESULTS_PER_PAGE,
    orderParam: defaultOrder,
    searchKeyParams: getSearchKey(activeFilterTab, TABS.CANCELED, searchKey),
    filters: normalizeFilters(
      {
        ...queryParams,
      },
      allFilterTranslations.subscriptions,
    ),
    enabled: activeFilterTab === TABS.CANCELED,
    setPage: updatePage,
  });

  const queryClient = useQueryClient();
  const resetAllSubscriptionToFirstPage = (): void => {
    updatePage(1);
    allSubscriptions.setOrder(defaultOrder);
    activeSubscriptions.setOrder(defaultOrder);
    canceledSubscriptions.setOrder(defaultOrder);
  };
  const refetchAllSubscriptions = (): void => {
    void queryClient.invalidateQueries({
      queryKey: [paywallSubscriptionQueryString],
    });
  };

  const subscriptionsDataByTab = {
    [TABS.ALL]: allSubscriptions,
    [TABS.ACTIVE]: activeSubscriptions,
    [TABS.CANCELED]: canceledSubscriptions,
  };

  return {
    subscriptionsDataByTab,
    resetAllSubscriptionToFirstPage,
    refetchAllSubscriptions,
    activeFilterTab,
    setActiveFilterTab: setActiveFilter,
    setSearchKey,
    isLoadingSubscriptions: allSubscriptions.isLoadingSubscriptions,
  };
};
